import { useState } from 'react';
import styles from './ResetPassword.module.css';
import * as Yup from 'yup';
import clsx from 'clsx';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { verifyForgotPasswordEmail } from '../../auth/_requests';
import { motion } from 'framer-motion';
import { useLogo } from '../../helpers/hooks/LogoContext';
import { useAuth } from '../../auth/Auth';
import eyeOpen from '../../assets/pages/login/eye.svg';
import eyeOff from '../../assets/pages/login/eyeClosed.svg';
import { showToast } from '../../helpers/utils/showToast';
import loginStyles from '../Login/Login.module.css';
import { AuthPageShell } from '../Login/AuthPageShell';

const loginSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, 'Minimum 8 characters')
    .max(15, 'Maximum 15 characters')
    .matches(/[a-z]/, 'Must contain at least one lowercase letter')
    .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
    .matches(/[0-9]/, 'Must contain at least one number')
    .matches(/[\W_]/, 'Must contain at least one special character')
    .matches(/^\S*$/, 'Password cannot contain spaces')
    .required('Password is required'),

  changePassword: Yup.string()
    .min(3, 'Minimum 8 characters')
    .max(50, 'Maximum 15 characters')
    .required('Password confirmation is required')
    .oneOf([Yup.ref('password')], "Password and Confirm Password didn't match"),
});

const initialValues = {
  password: '',
  changePassword: '',
};

export function ResetPassword() {
  const { logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { logoUrl } = useLogo();
  const { hash } = useParams();
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      try {
        const response = await verifyForgotPasswordEmail(hash, values.password);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        if (response.message === 'successful') {
          showToast({
            message: 'Password changed successfully, you can login with new password now',
            type: 'success',
          });
          setTimeout(() => {
            navigate('/login');
          }, 1500);
        } else {
          showToast({
            message: 'An error occurred. Please try again.',
            type: 'error',
          });
        }
      } catch (error) {
        console.error(error);
        showToast({
          message: 'New password cannot be the same as any of your last 4 passwords',
          type: 'error',
        });
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  return (
    <AuthPageShell variant='center'>
      <div className={loginStyles.loginCardShell}>
        <motion.main className={loginStyles.main} initial={false}>
          <div className={loginStyles.logoWrap}>
            <img src={logoUrl} className={loginStyles.logo} alt='logo' decoding='async' />
          </div>

          <div className={loginStyles.formTitle}>Choose a new password.</div>
          <p className={loginStyles.forgotText}>
            Set a new password for your account. You will use it the next time you sign in.
          </p>
          <form
            className={styles.form}
            onSubmit={formik.handleSubmit}
            noValidate
            id='kt_login_signin_form'
          >
            <div className={styles.formUpper}>
              <div className={styles.formBoxes}>
                <div className={styles.formBoxesInner}>
                  <div
                    className={'inputWrapper'}
                    title='Password must be 8-15 characters long, including at least one uppercase letter, one lowercase letter, one number, one special character, and no spaces.'
                  >
                    <div className={'label'}>New password</div>
                    <div
                      className={clsx(
                        formik.touched.password && formik.errors.password
                          ? loginStyles.isInvalid
                          : '',
                        formik.touched.password && !formik.errors.password ? loginStyles.valid : '',
                        'inputField'
                      )}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                        }
                      }}
                    >
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder={'••••••••'}
                        value={formik.values.password}
                        onChange={(e) => {
                          formik.setFieldValue('password', e.target.value);
                        }}
                        autoComplete='new-password'
                        className={loginStyles.passwordInputBox}
                        onBlur={formik.handleBlur('password')}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            formik.handleSubmit();
                          }
                        }}
                      />
                      <button
                        type='button'
                        onClick={(event) => {
                          event.preventDefault();
                          setShowPassword(!showPassword);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                          }
                        }}
                      >
                        <img
                          src={showPassword ? eyeOpen : eyeOff}
                          alt='eye-img'
                          className={loginStyles.eyeImg}
                        />
                      </button>
                    </div>
                    {formik.touched.password && formik.errors.password && (
                      <div className={loginStyles.emailError}>
                        <span>{formik.errors.password}</span>
                      </div>
                    )}
                  </div>
                  <div className={'inputWrapper'} title='Must match the password exactly'>
                    <div className={'label'}>Confirm new password</div>
                    <div
                      className={clsx(
                        formik.touched.changePassword && formik.errors.changePassword
                          ? loginStyles.isInvalid
                          : '',
                        formik.touched.changePassword && !formik.errors.changePassword
                          ? loginStyles.valid
                          : '',

                        'inputField'
                      )}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                        }
                      }}
                    >
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder={'••••••••'}
                        value={formik.values.changePassword}
                        onChange={(e) => {
                          formik.setFieldValue('changePassword', e.target.value);
                        }}
                        autoComplete='new-password'
                        className={loginStyles.passwordInputBox}
                        onBlur={formik.handleBlur('changePassword')}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            formik.handleSubmit();
                          }
                        }}
                      />
                      <button
                        type='button'
                        onClick={(event) => {
                          event.preventDefault();
                          setShowConfirmPassword(!showConfirmPassword);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                          }
                        }}
                      >
                        <img
                          src={showConfirmPassword ? eyeOpen : eyeOff}
                          alt='eye-img'
                          className={loginStyles.eyeImg}
                        />
                      </button>
                    </div>
                    {formik.touched.changePassword && formik.errors.changePassword && (
                      <div className={loginStyles.emailError}>
                        <div className='fv-help-block'>
                          <span>{formik.errors.changePassword}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <button
              type='submit'
              id='kt_sign_in_submit'
              disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
              className={`${loginStyles.submitBtn} ${
                (formik.isSubmitting || !formik.isValid || !formik.dirty) && loginStyles.disableBtn
              }`}
            >
              {!loading && <span className='indicator-label'>Change password</span>}
              {loading && (
                <span className={loginStyles.indicatorProgress} style={{ display: 'block' }}>
                  Please wait...
                  <span className={loginStyles.spinner}></span>
                </span>
              )}
            </button>
          </form>
          <div className={loginStyles.signInBox}>
            <Link to={'/login'} className={loginStyles.forgotPassword} onClick={() => logout()}>
              Sign in
            </Link>
          </div>
        </motion.main>
      </div>
    </AuthPageShell>
  );
}
